<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useDevices } from '@/composables/useDevices';
import { useToast } from 'primevue/usetoast';

// import Tag from 'primevue/tag';
import Dropdown from 'primevue/dropdown';
import Dialog from 'primevue/dialog';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';

const router = useRouter();
const toast = useToast();
const confirm = useConfirm();
const devices = useDevices();

const deviceList = ref([]);
const deviceTypes = ref([]);
const isLoading = ref(true);
const searchQuery = ref('');
const layout = ref('grid');

// Provision dialog
const showProvisionDialog = ref(false);
const provisionForm = ref({
  name: '',
  hardware_id: '',
  device_type: '',
  location: '',
});
const isProvisioning = ref(false);

// Credentials dialog (shown after provisioning)
const showCredentialsDialog = ref(false);
const deviceCredentials = ref(null);

onMounted(async () => {
  await Promise.all([
    fetchDevices(),
    fetchDeviceTypes(),
  ]);
});

const fetchDevices = async () => {
  isLoading.value = true;
  try {
    deviceList.value = await devices.getDevices();
    console.log(deviceList.value);
  } catch (err) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to fetch devices', life: 3000 });
  } finally {
    isLoading.value = false;
  }
};

const fetchDeviceTypes = async () => {
  try {
    const types = await devices.getDeviceTypes();
    deviceTypes.value = Array.isArray(types)
      ? types.map((t) => (typeof t === 'string' ? { label: t, value: t } : t))
      : [];
  } catch {
    deviceTypes.value = [];
  }
};

const filteredDevices = computed(() => {
  if (!searchQuery.value) return deviceList.value;
  const query = searchQuery.value.toLowerCase();
  return deviceList.value.filter(d =>
    d.name?.toLowerCase().includes(query) ||
    d.hardware_id?.toLowerCase().includes(query) ||
    d.location?.toLowerCase().includes(query)
  );
});

const openProvisionDialog = () => {
  provisionForm.value = { name: '', hardware_id: '', device_type: '', location: '' };
  showProvisionDialog.value = true;
};

const provisionDevice = async () => {
  if (!provisionForm.value.name || !provisionForm.value.hardware_id || !provisionForm.value.device_type) {
    toast.add({ severity: 'warn', summary: 'Validation', detail: 'Please fill all required fields', life: 3000 });
    return;
  }

  isProvisioning.value = true;
  try {
    const result = await devices.provisionDevice(provisionForm.value);
    showProvisionDialog.value = false;
    
    // Show credentials dialog
    deviceCredentials.value = result;
    showCredentialsDialog.value = true;
    
    await fetchDevices();
    toast.add({ severity: 'success', summary: 'Success', detail: 'Device provisioned successfully', life: 3000 });
  } catch (err) {
    toast.add({ severity: 'error', summary: 'Error', detail: err.message || 'Failed to provision device', life: 3000 });
  } finally {
    isProvisioning.value = false;
  }
};

const copyCredential = (value, label) => {
  navigator.clipboard.writeText(value);
  toast.add({ severity: 'success', summary: 'Copied', detail: `${label} copied to clipboard`, life: 2000 });
};

const goToDevice = (deviceOrId) => {
  // const id = typeof deviceOrId === 'object' && deviceOrId !== null ? (deviceOrId.id ?? deviceOrId.deviceId) : deviceOrId;
const id = deviceOrId.id
  console.log(deviceOrId)
  if (!id) {
    console.error("Device ID not parsed");
    toast.add({ severity: 'warn', summary: 'Missing ID', detail: 'Device ID is missing. Refresh and try again.', life: 3000 });
    return;
  }

  router.push({ name: 'Device Detail', params: { id: String(id) } });
};

const confirmDeleteDevice = (device) => {
  confirm.require({
    message: `Are you sure you want to delete "${device.name}"? This action cannot be undone.`,
    header: 'Delete Device',
    icon: 'fas fa-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        await devices.deleteDevice(device.id);
        await fetchDevices();
        toast.add({ severity: 'success', summary: 'Success', detail: 'Device deleted', life: 3000 });
      } catch (err) {
        toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete device', life: 3000 });
      }
    },
  });
};

// const getStatusSeverity = (status) => {
//   const map = {
//     online: 'success',
//     offline: 'danger',
//     pending: 'warning',
//     provisioning: 'info',
//   };
//   return map[status] || 'secondary';
// };

// const getDeviceIcon = (deviceType) => {
//   const icons = {
//     // Microcontrollers
//     esp32: 'fa-microchip',
//     esp8266: 'fa-microchip',
//     arduino: 'fa-microchip',
//
//     // SBCs / Servers
//     raspberry_pi: 'fa-server',
//     raspberry_pi_pico: 'fa-microchip', // Pico is a microcontroller, not a server
//
//     // Generic / Fallback
//     sensor_hub: 'fa-satellite-dish',
//     gateway: 'fa-network-wired'
//   };
//   return icons[deviceType] || 'fa-cube';
// };
const getDeviceIconObj = (deviceType) => {
  const map = {
    raspberry_pi:      { icon: 'fa-raspberry-pi', prefix: 'fab' }, // Brand icon
    esp32:             { icon: 'fa-wifi',         prefix: 'fas' },
    default:           { icon: 'fa-cube',         prefix: 'fas' }
  };

  return map[deviceType] || map.default;
};

const getActivePins = (device) => {
  if (!device.pins) return [];
  return device.pins.filter(p => p.enabled && (p.label || p.sensorType));
};

// const formatDate = (dateStr) => {
//   if (!dateStr) return 'Never';
//   return new Date(dateStr).toLocaleString();
// };

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return 'Unknown';

  const now = new Date();
  const diffMs = now - date;
  if (diffMs < 0) return 'Just now';

  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;

  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;

  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;

  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;

  const week = Math.floor(day / 7);
  if (week < 5) return `${week}w ago`;

  const month = Math.floor(day / 30);
  if (month < 12) return `${month}mo ago`;

  const year = Math.floor(day / 365);
  return `${year}y ago`;
};
</script>

<template>
  <div class="py-4 container-fluid">
    <ConfirmDialog />

    <div class="device-list-page">
      <!-- Header -->
      <div class="page-header">
        <div class="header-content">
          <div class="header-icon">
            <i class="fas fa-microchip"></i>
          </div>
          <div class="header-text">
            <h1>Device Studio</h1>
            <p>Visualise hardware, pin maps, and configurations</p>
          </div>
        </div>
        <div class="header-actions">
          <button class="btn-secondary" @click="fetchDevices">
            <i class="fas fa-sync-alt"></i>
            Refresh
          </button>
          <button class="btn-provision" @click="openProvisionDialog">
            <i class="fas fa-plus"></i>
            Provision Device
          </button>
        </div>
      </div>

      <!-- Search & Filters -->
      <div class="filters-bar">
        <div class="search-box">
          <i class="fas fa-search"></i>
          <input v-model="searchQuery" placeholder="Search devices..." class="search-input" />
        </div>
        <div class="layout-toggle">
          <button :class="{ active: layout === 'grid' }" @click="layout = 'grid'">
            <i class="fas fa-th"></i>
          </button>
          <button :class="{ active: layout === 'list' }" @click="layout = 'list'">
            <i class="fas fa-list"></i>
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <i class="fas fa-circle-notch fa-spin"></i>
        <span>Loading devices...</span>
      </div>

      <!-- Empty State -->
      <div v-else-if="!deviceList.length" class="empty-state">
        <div class="empty-icon">
          <i class="fas fa-microchip"></i>
        </div>
        <h3>No devices yet</h3>
        <p>Provision your first device to start monitoring</p>
        <button class="btn-provision" @click="openProvisionDialog">
          <i class="fas fa-plus"></i>
          Provision Device
        </button>
      </div>

      <!-- Device Grid -->
      <div v-else class="device-grid" :class="{ 'list-view': layout === 'list' }">
        <div
            v-for="device in filteredDevices"
            :key="device.id"
            class="device-card"
            @click="goToDevice(device)"
        >
          <div class="card-header-visual">
            <div class="device-identity">
              <div class="device-icon-wrapper" :class="device.status">
<!--                <i :class="['fas', getDeviceIcon(device.device_type)]"></i>-->
                <i :class="[getDeviceIconObj(device.device_type).prefix,getDeviceIconObj(device.device_type).icon]"></i>
              </div>
              <div class="device-names">
                <h3>{{ device.name }}</h3>
                <span class="device-model">{{ device.device_type_label }}</span>
              </div>
            </div>
            <div class="device-status-badge" :class="device.status">
              <span class="status-dot"></span>
              {{ device.status }}
            </div>
          </div>

          <div class="card-body">
            <div class="active-sensors-list">
              <div class="section-label">Active Connections</div>

              <div v-if="getActivePins(device).length > 0" class="sensor-tags">
                <div
                    v-for="pin in getActivePins(device).slice(0, 4)"
                    :key="pin.id"
                    class="sensor-tag"
                >
                  <span class="pin-id">{{ pin.pinIdentifier }}</span>
                  <span class="sensor-name">{{ pin.label || pin.sensorType || 'Active' }}</span>
                </div>
                <div v-if="getActivePins(device).length > 4" class="more-tag">
                  +{{ getActivePins(device).length - 4 }} more
                </div>
              </div>

              <div v-else class="empty-state">
                No sensors configured
              </div>
            </div>

            <div class="vital-stats">
              <div class="stat-item">
                <i class="fas fa-microchip"></i>
                <span>{{ device.firmware_version || 'v1.0' }}</span>
              </div>
              <div class="stat-item" title="Last Seen">
                <i class="fas fa-clock"></i>
                <span>{{ formatRelativeTime(device.last_seen) }}</span>
              </div>
              <div class="stat-item" title="WiFi Signal">
                <i class="fas fa-wifi"></i>
                <span>{{ device.wifi_rssi ? device.wifi_rssi + 'dBm' : '--' }}</span>
              </div>
            </div>
          </div>

          <div class="device-card-actions">
            <button class="btn-icon" @click.stop="goToDevice(device)" title="Configure Board">
              <i class="fas fa-sliders-h"></i>
            </button>
            <button class="btn-icon danger" @click.stop="confirmDeleteDevice(device)" title="Delete">
              <i class="fas fa-trash-alt"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Provision Dialog -->
    <Dialog 
      v-model:visible="showProvisionDialog" 
      header="Provision New Device" 
      :style="{ width: '500px' }"
      modal
      class="device-dialog"
    >
      <div class="provision-form">
        <div class="form-group">
          <label>Device Name *</label>
          <InputText v-model="provisionForm.name" placeholder="Living Room Sensor Hub" />
        </div>
        
        <div class="form-group">
          <label>Hardware ID *</label>
          <InputText v-model="provisionForm.hardware_id" placeholder="ESP32_A1B2C3D4" />
          <small>Unique identifier from your device (MAC address, serial number, etc.)</small>
        </div>
        
        <div class="form-group">
          <label>Device Type *</label>
          <Dropdown 
            v-model="provisionForm.device_type" 
            :options="deviceTypes"
            optionLabel="label"
            optionValue="value"
            placeholder="Select device type"
            class="w-full"
          />
        </div>
        
        <div class="form-group">
          <label>Location</label>
          <InputText v-model="provisionForm.location" placeholder="Living Room" />
        </div>

        <div class="form-actions">
          <button class="btn-secondary" @click="showProvisionDialog = false">Cancel</button>
          <button class="btn-primary" @click="provisionDevice" :disabled="isProvisioning">
            <i v-if="isProvisioning" class="fas fa-circle-notch fa-spin"></i>
            <i v-else class="fas fa-plus"></i>
            {{ isProvisioning ? 'Provisioning...' : 'Provision Device' }}
          </button>
        </div>
      </div>
    </Dialog>

    <!-- Credentials Dialog -->
    <Dialog 
      v-model:visible="showCredentialsDialog" 
      header="Device Credentials" 
      :style="{ width: '520px' }"
      modal
      :closable="false"
      class="device-dialog credentials-dialog"
    >
      <div v-if="deviceCredentials" class="credentials-content">
        <div class="success-icon">
          <i class="fas fa-check-circle"></i>
        </div>
        <h3>Device Provisioned Successfully!</h3>
        <p class="warning-text">
          <i class="fas fa-exclamation-triangle"></i>
          Save these credentials now. The API key will not be shown again.
        </p>

        <div class="credential-item">
          <label>MQTT Client ID</label>
          <div class="credential-value">
            <code>{{ deviceCredentials.mqtt_client_id }}</code>
            <button @click="copyCredential(deviceCredentials.mqtt_client_id, 'MQTT Client ID')">
              <i class="fas fa-copy"></i>
            </button>
          </div>
        </div>

        <div class="credential-item">
          <label>API Key</label>
          <div class="credential-value">
            <code>{{ deviceCredentials.api_key }}</code>
            <button @click="copyCredential(deviceCredentials.api_key, 'API Key')">
              <i class="fas fa-copy"></i>
            </button>
          </div>
        </div>

        <div class="credential-item" v-if="deviceCredentials.mqtt_topic">
          <label>MQTT Topic</label>
          <div class="credential-value">
            <code>{{ deviceCredentials.mqtt_topic }}</code>
            <button @click="copyCredential(deviceCredentials.mqtt_topic, 'MQTT Topic')">
              <i class="fas fa-copy"></i>
            </button>
          </div>
        </div>

        <div class="credential-actions">
          <button class="btn-primary" @click="showCredentialsDialog = false">
            <i class="fas fa-check"></i>
            I've Saved These Credentials
          </button>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<style scoped>
.device-list-page {
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: var(--calid-card-bg, rgba(17, 24, 39, 0.8));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
  border-radius: 16px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-icon {
  width: 56px;
  height: 56px;
  background: hsla(0, 0%, 100%, 0.15);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon i {
  font-size: 1.5rem;
  color: white;
}

.header-text h1 {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--calid-text-primary, #f0f0f5);
  margin: 0;
}

.header-text p {
  font-size: 0.9rem;
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0.25rem 0 0;
}

.btn-provision {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: #00d4ff;
  color: #0a0a0f;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-provision:hover {
  background: #00b8e0;
}

.filters-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
}

.search-box {
  position: relative;
  flex: 1;
  max-width: 400px;
}

.search-box i {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--calid-text-secondary, #94a3b8);
}

.search-box .search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.03));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.1));
  border-radius: 10px;
  color: var(--calid-text-primary, #f0f0f5);
}

.layout-toggle {
  display: flex;
  gap: 0.25rem;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.03));
  border-radius: 8px;
  padding: 0.25rem;
}

.layout-toggle button {
  padding: 0.5rem 0.75rem;
  border: none;
  background: transparent;
  color: var(--calid-text-secondary, #94a3b8);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.layout-toggle button.active {
  background: rgba(0, 212, 255, 0.15);
  color: #00d4ff;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
}

.loading-state {
  gap: 1rem;
  color: var(--calid-text-secondary, #94a3b8);
}

.loading-state i {
  font-size: 2rem;
  color: #00d4ff;
}

.empty-icon {
  width: 80px;
  height: 80px;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.03));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.empty-icon i {
  font-size: 2rem;
  color: var(--calid-text-secondary, #94a3b8);
}

.empty-state h3 {
  font-size: 1.25rem;
  color: var(--calid-text-primary, #f0f0f5);
  margin: 0 0 0.5rem;
}

.empty-state p {
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0 0 1.5rem;
}

.device-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.25rem;
}

.device-grid.list-view {
  grid-template-columns: 1fr;
}

.device-grid.list-view .device-card {
  display: grid;
  grid-template-columns: auto 1fr auto auto auto;
  align-items: center;
  gap: 1.5rem;
}

.device-card {
  background: var(--calid-card-bg, rgba(17, 24, 39, 0.8));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
  border-radius: 16px;
  padding: 1.25rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.device-card:hover {
  border-color: rgba(0, 212, 255, 0.3);
  transform: translateY(-2px);
}

.device-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.device-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.05));
}

.device-icon.online {
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
}

.device-icon.offline {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}

.device-icon.pending {
  background: rgba(245, 158, 11, 0.15);
  color: #fbbf24;
}

.device-icon i {
  font-size: 1.25rem;
}

.device-info h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--calid-text-primary, #f0f0f5);
  margin: 0 0 0.25rem;
}

.device-type {
  font-size: 0.85rem;
  color: #00d4ff;
  margin: 0 0 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.device-location {
  font-size: 0.85rem;
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.device-location i {
  font-size: 0.75rem;
}

.device-stats {
  display: flex;
  gap: 1.5rem;
  margin: 1rem 0;
  padding: 1rem 0;
  border-top: 1px solid var(--calid-border, rgba(255, 255, 255, 0.06));
  border-bottom: 1px solid var(--calid-border, rgba(255, 255, 255, 0.06));
}

.stat {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--calid-text-primary, #f0f0f5);
}

.stat-label {
  font-size: 0.75rem;
  color: var(--calid-text-secondary, #94a3b8);
}

.device-meta {
  margin-bottom: 1rem;
}

.last-seen {
  font-size: 0.8rem;
  color: var(--calid-text-tertiary, #64748b);
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.device-actions {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.05));
  color: var(--calid-text-secondary, #94a3b8);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-btn:hover {
  background: rgba(0, 212, 255, 0.15);
  color: #00d4ff;
}

.action-btn.danger:hover {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}

/* Provision Dialog */
.provision-form {
  padding: 0.5rem 0;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--calid-text-secondary, #94a3b8);
  margin-bottom: 0.5rem;
}

.form-group :deep(.p-inputtext),
.form-group :deep(.p-dropdown) {
  width: 100%;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.03));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.1));
  border-radius: 10px;
  color: var(--calid-text-primary, #f0f0f5);
}

.form-group :deep(.p-inputtext) {
  padding: 0.75rem 1rem;
}

.form-group small {
  display: block;
  margin-top: 0.375rem;
  font-size: 0.8rem;
  color: var(--calid-text-tertiary, #64748b);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
}

.btn-primary,
.btn-secondary {
  padding: 0.75rem 1.25rem;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.btn-primary {
  background: #00d4ff;
  color: #0a0a0f;
}

.btn-primary:hover:not(:disabled) {
  background: #00b8e0;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.05));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.1));
  color: var(--calid-text-secondary, #94a3b8);
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Credentials Dialog */
.credentials-content {
  text-align: center;
  padding: 1rem 0;
}

.success-icon {
  width: 72px;
  height: 72px;
  margin: 0 auto 1.5rem;
  background: rgba(16, 185, 129, 0.15);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.success-icon i {
  font-size: 2rem;
  color: #34d399;
}

.credentials-content h3 {
  font-size: 1.25rem;
  color: var(--calid-text-primary, #f0f0f5);
  margin: 0 0 0.75rem;
}

.warning-text {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.2);
  border-radius: 8px;
  color: #fbbf24;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
}

.credential-item {
  text-align: left;
  margin-bottom: 1rem;
}

.credential-item label {
  display: block;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--calid-text-secondary, #94a3b8);
  margin-bottom: 0.5rem;
}

.credential-value {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.03));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.1));
  border-radius: 8px;
  padding: 0.75rem;
}

.credential-value code {
  flex: 1;
  font-family: 'JetBrains Mono', 'SF Mono', monospace;
  font-size: 0.85rem;
  color: #00d4ff;
  word-break: break-all;
}

.credential-value button {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: var(--calid-text-secondary, #94a3b8);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.credential-value button:hover {
  background: rgba(0, 212, 255, 0.15);
  color: #00d4ff;
}

.credential-actions {
  margin-top: 1.5rem;
}

.credential-actions .btn-primary {
  width: 100%;
  justify-content: center;
}

/* Dialog overrides */
:deep(.device-dialog .p-dialog) {
  background: var(--calid-card-bg, #111827);
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
  border-radius: 20px;
}

:deep(.device-dialog .p-dialog-header) {
  background: transparent;
  border-bottom: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
  padding: 1.25rem 1.5rem;
  color: var(--calid-text-primary, #f0f0f5);
}

:deep(.device-dialog .p-dialog-content) {
  background: transparent;
  padding: 1.5rem !important;
}

/* Light mode */
:root:not(.dark-version) .page-header,
:root:not(.dark-version) .device-card {
  background: white;
  border-color: #e5e7eb;
}

:root:not(.dark-version) .header-text h1,
:root:not(.dark-version) .device-info h3,
:root:not(.dark-version) .stat-value {
  color: #1f2937;
}

:root:not(.dark-version) .header-text p,
:root:not(.dark-version) .device-location,
:root:not(.dark-version) .stat-label {
  color: #6b7280;
}
</style>
